import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';

import { API_URL } from '../../config/api';
import WorkloopBroadcastPanel from './WorkloopBroadcastPanel';
import ActivityTracker from './ActivityTracker/ActivityTracker';
import AppUpdateConfigPanel from './AppUpdateConfigPanel';
import AdminRunScriptPanel from './AdminRunScriptPanel';
import AdminJiraSettingsPanel from './AdminJiraSettingsPanel';
import OrganizationRolesPanel from './OrganizationRolesPanel';
import OrgWorkflowReactionsPanel from './OrgWorkflowReactionsPanel';
import AdminNewsPanel from './AdminNewsPanel';
import {
  MEMBERSHIP_SELECT_CLASS,
  supervisorGroups,
  userLabel,
} from '../../utils/orgMembershipUtils';
import PresignedAvatar from '../common/PresignedAvatar';

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const AdminPanel = () => {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'password-resets', 'all-users', 'broadcasts', 'activity'
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [newPasswordForReset, setNewPasswordForReset] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [userTotal, setUserTotal] = useState(0);
  const [userSort, setUserSort] = useState('createdAt,desc');
  const [editingUser, setEditingUser] = useState(null);
  const [pendingDrafts, setPendingDrafts] = useState({});
  const [orgRoles, setOrgRoles] = useState([]);
  const [editDraft, setEditDraft] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    organizationId: '',
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const emptyInviteDraft = {
    name: '',
    email: '',
    gender: '',
    organizationRoleId: '',
    supervisorUserId: '',
  };
  const [inviteDraft, setInviteDraft] = useState(emptyInviteDraft);
  const [createDraft, setCreateDraft] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    dateOfBirth: '',
    organizationId: '',
  });
  const [organizations, setOrganizations] = useState([]);
  const USER_PAGE_SIZE = 25;

  const organizationLabel = (userItem) => {
    if (userItem?.organizationName) return userItem.organizationName;
    const org = organizations.find((item) => item.id === userItem?.organizationId);
    if (org?.organizationName) return org.organizationName;
    if (org?.organizationSlug) return org.organizationSlug;
    return userItem?.organizationId ? 'Unknown organization' : '—';
  };

  const formatCreatedAt = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };
  const isPlatformAdmin = user?.role === 'ADMIN';
  const isOrgAdmin = user?.role === 'ORG_ADMIN';
  const canManageMembership = isPlatformAdmin || isOrgAdmin || Boolean(user?.canAssignRoles);
  const assignableOrgRoles = orgRoles.filter((role) => isOrgAdmin || isPlatformAdmin || role.assignable);

  const membershipLabel = (item) => {
    if (item?.role === 'ADMIN') return 'Platform admin';
    if (item?.role === 'ORG_ADMIN') return 'Org admin';
    return item?.organizationRoleName || 'Member';
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchPendingUsers();
      fetchAllUsers();
      fetchOrgRoles();
    } else if (activeTab === 'password-resets') {
      fetchPasswordResetRequests();
    } else if (activeTab === 'all-users') {
      const timer = setTimeout(() => {
        fetchAllUsers();
        fetchOrgRoles();
        if (isPlatformAdmin) {
          fetchOrganizations();
        }
      }, 250);
      return () => clearTimeout(timer);
    } else if (activeTab === 'org-roles' || activeTab === 'org-workflows' || activeTab === 'scripts' || activeTab === 'integrations' || activeTab === 'news') {
      setLoading(false);
    }
  }, [activeTab, userSearch, userPage, userSort]);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/pending-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPendingUsers(response.data);
      const nextDrafts = {};
      (Array.isArray(response.data) ? response.data : []).forEach((item) => {
        nextDrafts[item.id] = {
          organizationRoleId: item.organizationRoleId || '',
          supervisorUserId: item.supervisorUserId || '',
        };
      });
      setPendingDrafts(nextDrafts);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to fetch pending users'
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgRoles = async () => {
    if (isPlatformAdmin) {
      setOrgRoles([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/organization-roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrgRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setOrgRoles([]);
    }
  };

  const fetchOrganizations = async () => {
    if (!isPlatformAdmin) {
      setOrganizations([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setOrganizations(list);
    } catch (error) {
      setOrganizations([]);
    }
  };

  const higherSupervisors = (excludeUserId, roleId) =>
    supervisorGroups(allUsers, { excludeUserId, roleId, orgRoles })
      .flatMap((group) => group.people);

  const handleApprove = async (userId) => {
    const draft = pendingDrafts[userId] || { organizationRoleId: '', supervisorUserId: '' };
    const promoteOrgAdmin = draft.organizationRoleId === '__org_admin__';
    if (!promoteOrgAdmin && !draft.organizationRoleId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose a role before approving this member',
      }));
      return;
    }
    const higher = higherSupervisors(userId, draft.organizationRoleId);
    if (!promoteOrgAdmin && higher.length > 0 && !draft.supervisorUserId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose a supervisor with a higher role',
      }));
      return;
    }
    try {
      setProcessing({ ...processing, [userId]: 'approving' });
      await axios.post(`${API_URL}/admin/approve-user/${userId}`, {
        role: promoteOrgAdmin ? 'ORG_ADMIN' : 'USER',
        organizationRoleId: promoteOrgAdmin ? '' : draft.organizationRoleId,
        supervisorUserId: draft.supervisorUserId || '',
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      dispatch(addNotification({
        type: 'success',
        message: 'User approved successfully'
      }));
      
      // Remove from pending list
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to approve user:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to approve user'
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessing({ ...processing, [userId]: 'rejecting' });
      await axios.delete(`${API_URL}/admin/reject-user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      dispatch(addNotification({
        type: 'success',
        message: 'User rejected and deleted successfully'
      }));
      
      // Remove from pending list
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to reject user:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to reject user'
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const fetchPasswordResetRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/password-reset-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPasswordResetRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch password reset requests:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to fetch password reset requests'
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const paged = activeTab === 'all-users';
      const trimmed = userSearch.trim();
      const digits = trimmed.replace(/\D/g, '');
      const searchTerm = digits.length >= 6 && digits.length === trimmed.replace(/\s/g, '').length
        ? digits
        : trimmed;
      const params = paged
        ? {
            page: userPage,
            size: USER_PAGE_SIZE,
            sort: userSort,
            ...(searchTerm ? { search: searchTerm } : {}),
          }
        : undefined;
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params,
      });
      const payload = response.data;
      const list = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.items) ? payload.items : []);
      setAllUsers(list);
      setUserTotal(Array.isArray(payload) ? list.length : Number(payload?.total || 0));
    } catch (error) {
      console.error('Failed to fetch all users:', error);
      const errorMessage = typeof error.response?.data === 'string' 
        ? error.response.data 
        : (error.response?.data?.error || error.message || 'Failed to fetch users');
      dispatch(addNotification({
        type: 'error',
        message: errorMessage
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePasswordReset = async (requestId) => {
    try {
      setProcessing({ ...processing, [requestId]: 'approving' });
      await axios.post(`${API_URL}/admin/approve-password-reset/${requestId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      dispatch(addNotification({
        type: 'success',
        message: 'Password reset approved. User can now set their new password using the request ID.'
      }));
      
      setPasswordResetRequests(passwordResetRequests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Failed to approve password reset:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to approve password reset'
      }));
    } finally {
      setProcessing({ ...processing, [requestId]: null });
    }
  };

  const handleRejectPasswordReset = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this password reset request?')) {
      return;
    }

    try {
      setProcessing({ ...processing, [requestId]: 'rejecting' });
      await axios.post(`${API_URL}/admin/reject-password-reset/${requestId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      dispatch(addNotification({
        type: 'success',
        message: 'Password reset request rejected'
      }));
      
      setPasswordResetRequests(passwordResetRequests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Failed to reject password reset:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to reject password reset'
      }));
    } finally {
      setProcessing({ ...processing, [requestId]: null });
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      setProcessing({ ...processing, [userId]: 'activating' });
      const response = await axios.post(`${API_URL}/admin/activate-user/${userId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Activate user response:', response.data);
      
      // Optimistically update the user in the list
      if (response.data) {
        setAllUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, active: true, ...response.data }
              : user
          )
        );
      }
      
      dispatch(addNotification({
        type: 'success',
        message: 'User activated successfully'
      }));
      
      // Refresh the users list to ensure consistency
      setTimeout(() => {
        fetchAllUsers();
      }, 500);
    } catch (error) {
      console.error('Failed to activate user:', error);
      console.error('Error response:', error.response);
      const errorMessage = typeof error.response?.data === 'string' 
        ? error.response.data 
        : (error.response?.data?.error || error.message || 'Failed to activate user');
      dispatch(addNotification({
        type: 'error',
        message: errorMessage
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will not be able to login.')) {
      return;
    }

    try {
      setProcessing({ ...processing, [userId]: 'deactivating' });
      const response = await axios.post(`${API_URL}/admin/deactivate-user/${userId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Deactivate user response:', response.data);
      
      // Optimistically update the user in the list
      if (response.data) {
        setAllUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, active: false, ...response.data }
              : user
          )
        );
      }
      
      dispatch(addNotification({
        type: 'success',
        message: 'User deactivated successfully'
      }));
      
      // Refresh the users list to ensure consistency
      setTimeout(() => {
        fetchAllUsers();
      }, 500);
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      console.error('Error response:', error.response);
      const errorMessage = typeof error.response?.data === 'string' 
        ? error.response.data 
        : (error.response?.data?.error || error.message || 'Failed to deactivate user');
      dispatch(addNotification({
        type: 'error',
        message: errorMessage
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const handleGrantAnnouncementAccess = async (userId) => {
    try {
      setProcessing({ ...processing, [userId]: 'granting-access' });
      const response = await axios.post(`${API_URL}/admin/grant-announcement-access/${userId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Grant announcement access response:', response.data);
      
      // Optimistically update the user in the list
      if (response.data) {
        setAllUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, hasAnnouncementAccess: true, ...response.data }
              : user
          )
        );
      }
      
      dispatch(addNotification({
        type: 'success',
        message: 'Announcement access granted successfully'
      }));
      
      // Refresh the users list to ensure consistency
      setTimeout(() => {
        fetchAllUsers();
      }, 500);
    } catch (error) {
      console.error('Failed to grant announcement access:', error);
      console.error('Error response:', error.response);
      const errorMessage = typeof error.response?.data === 'string' 
        ? error.response.data 
        : (error.response?.data?.error || error.message || 'Failed to grant announcement access');
      dispatch(addNotification({
        type: 'error',
        message: errorMessage
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const handleRevokeAnnouncementAccess = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke announcement access from this user?')) {
      return;
    }

    try {
      setProcessing({ ...processing, [userId]: 'revoking-access' });
      const response = await axios.post(`${API_URL}/admin/revoke-announcement-access/${userId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Revoke announcement access response:', response.data);
      
      // Optimistically update the user in the list
      if (response.data) {
        setAllUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, hasAnnouncementAccess: false, ...response.data }
              : user
          )
        );
      }
      
      dispatch(addNotification({
        type: 'success',
        message: 'Announcement access revoked successfully'
      }));
      
      // Refresh the users list to ensure consistency
      setTimeout(() => {
        fetchAllUsers();
      }, 500);
    } catch (error) {
      console.error('Failed to revoke announcement access:', error);
      console.error('Error response:', error.response);
      const errorMessage = typeof error.response?.data === 'string' 
        ? error.response.data 
        : (error.response?.data?.error || error.message || 'Failed to revoke announcement access');
      dispatch(addNotification({
        type: 'error',
        message: errorMessage
      }));
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const formatPhone = (userItem) => {
    const raw = String(userItem?.mobileNumber || userItem?.contactNumber || '').replace(/\D/g, '');
    if (!raw) return '—';
    return raw.length > 10 ? raw.slice(-10) : raw;
  };

  const openEditUser = (userItem) => {
    setEditingUser(userItem);
    setEditDraft({
      firstName: userItem.firstName || '',
      lastName: userItem.lastName || '',
      username: userItem.username || '',
      email: userItem.email || '',
      phone: formatPhone(userItem) === '—' ? '' : formatPhone(userItem),
      dateOfBirth: userItem.dateOfBirth || '',
    });
  };

  const closeEditUser = () => {
    setEditingUser(null);
  };

  const handleSaveUserInfo = async () => {
    if (!editingUser) return;
    const firstName = String(editDraft.firstName || '').trim();
    const lastName = String(editDraft.lastName || '').trim();
    const username = String(editDraft.username || '').trim();
    const email = String(editDraft.email || '').trim();
    const mobile = String(editDraft.phone || '').replace(/\D/g, '');
    const dateOfBirth = String(editDraft.dateOfBirth || '').trim();
    if (!firstName || !lastName || !username || !email) {
      dispatch(addNotification({
        type: 'error',
        message: 'First name, last name, username, and email are required',
      }));
      return;
    }
    if (mobile && mobile.length !== 10) {
      dispatch(addNotification({
        type: 'error',
        message: 'Enter a valid 10-digit phone number, or leave it blank',
      }));
      return;
    }
    try {
      setProcessing({ ...processing, [editingUser.id]: 'updating-user' });
      const response = await axios.put(
        `${API_URL}/admin/users/${editingUser.id}`,
        { firstName, lastName, username, email, mobile, dateOfBirth },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = response.data || {};
      setAllUsers((prev) => prev.map((row) => (row.id === editingUser.id ? { ...row, ...updated } : row)));
      dispatch(addNotification({
        type: 'success',
        message: `Updated @${updated.username || username}`,
      }));
      closeEditUser();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to update user',
      }));
    } finally {
      setProcessing({ ...processing, [editingUser.id]: null });
    }
  };

  const resetCreateDraft = () => {
    setCreateDraft({
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      dateOfBirth: '',
      organizationId: '',
    });
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const firstName = String(createDraft.firstName || '').trim();
    const lastName = String(createDraft.lastName || '').trim();
    const username = String(createDraft.username || '').trim();
    const email = String(createDraft.email || '').trim();
    const mobile = String(createDraft.phone || '').replace(/\D/g, '');
    const password = String(createDraft.password || '');
    const dateOfBirth = String(createDraft.dateOfBirth || '').trim();
    const organizationId = String(createDraft.organizationId || '').trim();
    if (!firstName || !lastName || !email || !password) {
      dispatch(addNotification({
        type: 'error',
        message: 'First name, last name, email, and password are required',
      }));
      return;
    }
    if (password.length < 6) {
      dispatch(addNotification({
        type: 'error',
        message: 'Password must be at least 6 characters',
      }));
      return;
    }
    if (mobile.length !== 10) {
      dispatch(addNotification({
        type: 'error',
        message: 'Enter a valid 10-digit phone number',
      }));
      return;
    }
    if (!organizationId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose which organization this user should join',
      }));
      return;
    }
    try {
      setCreatingUser(true);
      const response = await axios.post(
        `${API_URL}/admin/users`,
        { firstName, lastName, username, email, password, mobile, dateOfBirth, organizationId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const created = response.data || {};
      dispatch(addNotification({
        type: 'success',
        message: `Created @${created.username || username || email}. They can log in now.`,
      }));
      resetCreateDraft();
      setCreateOpen(false);
      setUserPage(0);
      fetchAllUsers();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to create user',
      }));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleResendInvite = async (userItem) => {
    const userId = userItem?.id;
    if (!userId || processing[userId]) return;
    try {
      setProcessing((prev) => ({ ...prev, [userId]: 'resending' }));
      await axios.post(
        `${API_URL}/admin/users/${userId}/resend-invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      dispatch(addNotification({
        type: 'success',
        message: `Invite mail sent again to ${userItem.email || 'this person'}`,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to resend invite',
      }));
    } finally {
      setProcessing((prev) => ({ ...prev, [userId]: null }));
    }
  };

  const handleInviteUser = async (event) => {
    event.preventDefault();
    const name = String(inviteDraft.name || '').trim();
    const email = String(inviteDraft.email || '').trim();
    const gender = String(inviteDraft.gender || '').trim();
    const organizationRoleId = String(inviteDraft.organizationRoleId || '').trim();
    const supervisorUserId = String(inviteDraft.supervisorUserId || '').trim();
    const promoteOrgAdmin = organizationRoleId === '__org_admin__';
    const inviteSupervisors = supervisorGroups(allUsers, { roleId: organizationRoleId, orgRoles });
    const hasHigher = inviteSupervisors.some((group) => group.people.length > 0);
    if (!name || !email || !gender || !organizationRoleId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Fullname, email, gender, and role are required',
      }));
      return;
    }
    if (!promoteOrgAdmin && hasHigher && !supervisorUserId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose a supervisor with a higher role',
      }));
      return;
    }
    if (!promoteOrgAdmin && supervisorUserId
        && !inviteSupervisors.some((group) => group.people.some((person) => person.id === supervisorUserId))) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose a supervisor with a higher role',
      }));
      return;
    }
    try {
      setCreatingUser(true);
      const response = await axios.post(
        `${API_URL}/admin/users/invite`,
        {
          name,
          email,
          gender,
          organizationRoleId: promoteOrgAdmin ? '__org_admin__' : organizationRoleId,
          supervisorUserId: promoteOrgAdmin ? '' : supervisorUserId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const created = response.data || {};
      dispatch(addNotification({
        type: 'success',
        message: `Invite sent to ${created.email || email}. They can set a password from the email and then log in.`,
      }));
      setInviteDraft(emptyInviteDraft);
      setCreateOpen(false);
      setUserPage(0);
      fetchAllUsers();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to send invite',
      }));
    } finally {
      setCreatingUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading && activeTab !== 'scripts' && activeTab !== 'integrations' && activeTab !== 'all-users' && activeTab !== 'org-roles' && activeTab !== 'org-workflows') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          {user?.role !== 'ADMIN' && (
          <button
            onClick={() => navigate('/chat')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Chat
          </button>
          )}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Admin Panel
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage user registrations, password resets, and user accounts
          </p>
        </div>
        {user?.role === 'ADMIN' && (
          <button
            onClick={() => dispatch(logout())}
            className="flex items-center text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-x-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Pending Users
          </button>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('password-resets')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'password-resets'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Password Reset Requests
            </button>
          )}
          <button
            onClick={() => setActiveTab('all-users')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all-users'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            All Users
          </button>
          {isOrgAdmin && (
            <button
              onClick={() => setActiveTab('org-roles')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'org-roles'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Org roles
            </button>
          )}
          {(isOrgAdmin || isPlatformAdmin) && (
            <button
              onClick={() => setActiveTab('org-workflows')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'org-workflows'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Workflow tags
            </button>
          )}
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('broadcasts')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'broadcasts'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Broadcasts
            </button>
          )}
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Activity Tracker
          </button>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('app-updates')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'app-updates'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              App Updates
            </button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
            <button
              onClick={() => setActiveTab('news')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'news'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              News
            </button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
            <button
              onClick={() => setActiveTab('integrations')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'integrations'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Integrations
            </button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
            <button
              onClick={() => setActiveTab('scripts')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'scripts'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Run script
            </button>
          )}
          </div>
          </div>
      {/* Pending Users Tab */}
      {activeTab === 'users' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Pending User Approvals
            </h2>
            <button
              onClick={fetchPendingUsers}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Refresh
            </button>
          </div>
        </div>

        {pendingUsers.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pending users</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              All user registrations have been processed.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Supervisor
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {pendingUsers.map((pendingUser) => (
                  <tr key={pendingUser.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {pendingUser.firstName?.[0] || ''}{pendingUser.lastName?.[0] || ''}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {pendingUser.firstName} {pendingUser.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            @{pendingUser.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {pendingUser.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(pendingUser.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={pendingDrafts[pendingUser.id]?.organizationRoleId || ''}
                        onChange={(e) => {
                          const organizationRoleId = e.target.value;
                          const allowed = higherSupervisors(pendingUser.id, organizationRoleId).map((item) => item.id);
                          setPendingDrafts((prev) => {
                            const current = prev[pendingUser.id] || {};
                            const nextSupervisor = allowed.includes(current.supervisorUserId)
                              ? current.supervisorUserId
                              : '';
                            return {
                              ...prev,
                              [pendingUser.id]: { ...current, organizationRoleId, supervisorUserId: nextSupervisor },
                            };
                          });
                        }}
                        className={MEMBERSHIP_SELECT_CLASS}
                      >
                        <option value="">Select role</option>
                        {isOrgAdmin ? <option value="__org_admin__">Org admin</option> : null}
                        {assignableOrgRoles.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={pendingDrafts[pendingUser.id]?.supervisorUserId || ''}
                        onChange={(e) => setPendingDrafts((prev) => ({
                          ...prev,
                          [pendingUser.id]: { ...(prev[pendingUser.id] || {}), supervisorUserId: e.target.value },
                        }))}
                        className={MEMBERSHIP_SELECT_CLASS}
                      >
                        <option value="">
                          {supervisorGroups(allUsers, {
                            excludeUserId: pendingUser.id,
                            roleId: pendingDrafts[pendingUser.id]?.organizationRoleId || '',
                            orgRoles,
                          }).length
                            ? 'Select supervisor'
                            : 'No higher role available'}
                        </option>
                        {supervisorGroups(allUsers, {
                          excludeUserId: pendingUser.id,
                          roleId: pendingDrafts[pendingUser.id]?.organizationRoleId || '',
                          orgRoles,
                        }).map((group) => (
                          <optgroup key={group.key} label={group.name}>
                            {group.people.map((option) => (
                              <option key={option.id} value={option.id}>
                                {userLabel(option)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleApprove(pendingUser.id)}
                          disabled={processing[pendingUser.id]}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing[pendingUser.id] === 'approving' ? 'Approving...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(pendingUser.id)}
                          disabled={processing[pendingUser.id]}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing[pendingUser.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Password Reset Requests Tab */}
      {activeTab === 'password-resets' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Password Reset Requests
            </h2>
            <button
              onClick={fetchPasswordResetRequests}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : passwordResetRequests.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pending password reset requests</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              All password reset requests have been processed.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {passwordResetRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {request.firstName?.[0] || ''}{request.lastName?.[0] || ''}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {request.firstName} {request.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            @{request.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {request.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {request.reason || 'No reason provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(request.requestedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {request.id.substring(0, 8)}...
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        User will use this to set password
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleApprovePasswordReset(request.id)}
                          disabled={processing[request.id]}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing[request.id] === 'approving' ? 'Approving...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleRejectPasswordReset(request.id)}
                          disabled={processing[request.id]}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing[request.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'all-users' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              All Users
            </h2>
            <div className="flex items-center gap-2">
              {(isPlatformAdmin || isOrgAdmin) && (
                <button
                  type="button"
                  onClick={() => setCreateOpen((open) => !open)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                >
                  {createOpen ? 'Close form' : (isOrgAdmin ? 'Invite user' : 'Create user')}
                </button>
              )}
              <button
                onClick={fetchAllUsers}
                className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Refresh
              </button>
            </div>
          </div>
          {isOrgAdmin && createOpen && (
            <form
              onSubmit={handleInviteUser}
              className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-4 space-y-3"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Invite a teammate</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  They get an email with a link to set their password, then they can log in.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Fullname</span>
                  <input
                    required
                    value={inviteDraft.name}
                    onChange={(e) => setInviteDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Fullname"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Email</span>
                  <input
                    required
                    type="email"
                    value={inviteDraft.email}
                    onChange={(e) => setInviteDraft((d) => ({ ...d, email: e.target.value }))}
                    placeholder="Email"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Gender</span>
                  <select
                    required
                    value={inviteDraft.gender}
                    onChange={(e) => setInviteDraft((d) => ({ ...d, gender: e.target.value }))}
                    className={MEMBERSHIP_SELECT_CLASS}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Role</span>
                  <select
                    required
                    value={inviteDraft.organizationRoleId}
                    onChange={(e) => {
                      const organizationRoleId = e.target.value;
                      const allowed = higherSupervisors(undefined, organizationRoleId).map((item) => item.id);
                      setInviteDraft((d) => ({
                        ...d,
                        organizationRoleId,
                        supervisorUserId: allowed.includes(d.supervisorUserId) ? d.supervisorUserId : '',
                      }));
                    }}
                    className={MEMBERSHIP_SELECT_CLASS}
                  >
                    <option value="">Select role</option>
                    {isOrgAdmin ? <option value="__org_admin__">Org admin</option> : null}
                    {assignableOrgRoles.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Supervisor</span>
                  <select
                    required={inviteDraft.organizationRoleId !== '__org_admin__'
                      && supervisorGroups(allUsers, { roleId: inviteDraft.organizationRoleId, orgRoles }).length > 0}
                    disabled={!inviteDraft.organizationRoleId || inviteDraft.organizationRoleId === '__org_admin__'}
                    value={inviteDraft.supervisorUserId}
                    onChange={(e) => setInviteDraft((d) => ({ ...d, supervisorUserId: e.target.value }))}
                    className={MEMBERSHIP_SELECT_CLASS}
                  >
                    <option value="">
                      {!inviteDraft.organizationRoleId
                        ? 'Select a role first'
                        : inviteDraft.organizationRoleId === '__org_admin__'
                          ? 'Not required for org admin'
                          : (supervisorGroups(allUsers, {
                            roleId: inviteDraft.organizationRoleId,
                            orgRoles,
                          }).length
                            ? 'Select supervisor'
                            : 'No higher role available')}
                    </option>
                    {inviteDraft.organizationRoleId && inviteDraft.organizationRoleId !== '__org_admin__'
                      ? supervisorGroups(allUsers, {
                        roleId: inviteDraft.organizationRoleId,
                        orgRoles,
                      }).map((group) => (
                        <optgroup key={group.key} label={group.name}>
                          {group.people.map((option) => (
                            <option key={option.id} value={option.id}>
                              {userLabel(option)}
                            </option>
                          ))}
                        </optgroup>
                      ))
                      : null}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setInviteDraft(emptyInviteDraft);
                    setCreateOpen(false);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingUser}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {creatingUser ? 'Sending…' : 'Send invite'}
                </button>
              </div>
            </form>
          )}
          {isPlatformAdmin && createOpen && (
            <form
              onSubmit={handleCreateUser}
              className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-4 space-y-3"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Create a test user</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Skips OTP. The account is verified and can log in with this password.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  required
                  value={createDraft.firstName}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, firstName: e.target.value }))}
                  placeholder="First name"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  required
                  value={createDraft.lastName}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, lastName: e.target.value }))}
                  placeholder="Last name"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  value={createDraft.username}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, username: e.target.value }))}
                  placeholder="Username (optional)"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  required
                  type="email"
                  value={createDraft.email}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, email: e.target.value }))}
                  placeholder="Email"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  required
                  type="tel"
                  value={createDraft.phone}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, phone: e.target.value }))}
                  placeholder="Phone (10 digits)"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  required
                  type="password"
                  value={createDraft.password}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, password: e.target.value }))}
                  placeholder="Password (min 6)"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <input
                  type="date"
                  value={createDraft.dateOfBirth}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, dateOfBirth: e.target.value }))}
                  placeholder="Date of birth"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <label className="block sm:col-span-2">
                  <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Which organization should this user join?
                  </span>
                  <select
                    required
                    value={createDraft.organizationId}
                    onChange={(e) => setCreateDraft((d) => ({ ...d, organizationId: e.target.value }))}
                    className={MEMBERSHIP_SELECT_CLASS}
                  >
                    <option value="">Select organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.organizationName || org.organizationSlug || org.id}
                        {org.organizationSlug ? ` (${org.organizationSlug})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetCreateDraft();
                    setCreateOpen(false);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingUser}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {creatingUser ? 'Creating…' : 'Create user'}
                </button>
              </div>
            </form>
          )}
          <form
            className="flex flex-col lg:flex-row lg:items-center gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setUserPage(0);
              fetchAllUsers();
            }}
          >
            <input
              type="search"
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setUserPage(0);
              }}
              placeholder="Search name, username, email, or phone"
              className="w-full sm:max-w-md px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={userSort}
              onChange={(e) => {
                setUserSort(e.target.value);
                setUserPage(0);
              }}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
            >
              <option value="createdAt,desc">Newest created</option>
              <option value="createdAt,asc">Oldest created</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              Search
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {userTotal} user{userTotal === 1 ? '' : 's'} · {USER_PAGE_SIZE} per page
            </p>
          </form>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : allUsers.length === 0 ? (
          <div className="p-12 text-center">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {userSearch.trim() ? 'No users match this search.' : 'There are no users in the system.'}
            </p>
          </div>
        ) : isOrgAdmin ? (
          <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
            {allUsers.map((userItem) => {
              const displayName = [userItem.firstName, userItem.lastName].filter(Boolean).join(' ').trim()
                || userItem.username
                || 'Unknown';
              const canEdit = canManageMembership && userItem.role !== 'ADMIN'
                && (isOrgAdmin || isPlatformAdmin || userItem.role !== 'ORG_ADMIN');
              const resending = processing[userItem.id] === 'resending';
              return (
                <div
                  key={userItem.id}
                  className="flex min-h-[13.5rem] flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm"
                >
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 rounded-full p-1 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900">
                      <PresignedAvatar
                        fileKey={userItem.profilePicture}
                        cacheKey={userItem.id}
                        name={displayName}
                        className="w-[5.5rem] h-[5.5rem] rounded-full object-cover"
                        fallbackClassName="flex items-center justify-center w-[5.5rem] h-[5.5rem] rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-xl font-semibold"
                      />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-sm text-gray-900 dark:text-white">
                        <span className="font-semibold">Name :</span>{' '}
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/users/${userItem.id}`)}
                            className="text-emerald-600 dark:text-emerald-400 underline decoration-emerald-500/70 hover:text-emerald-700"
                          >
                            {displayName}
                          </button>
                        ) : (
                          <span>{displayName}</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 break-all">
                        <span className="font-semibold">Email :</span>{' '}
                        {userItem.email || '—'}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-semibold">Mobile :</span>{' '}
                        {formatPhone(userItem) === '—' ? '' : formatPhone(userItem)}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-semibold">Organization :</span>{' '}
                        {organizationLabel(userItem)}
                      </p>
                      <div className="pt-1">
                        <span className="inline-flex items-center rounded-full bg-rose-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          {membershipLabel(userItem)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto pt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/users/${userItem.id}`)}
                        className="text-emerald-600 dark:text-emerald-400 underline decoration-emerald-500/70 hover:text-emerald-700"
                      >
                        Edit profile
                      </button>
                    ) : null}
                    {canEdit && userItem.role !== 'ADMIN' ? (
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                    ) : null}
                    {userItem.role !== 'ADMIN' ? (
                      <button
                        type="button"
                        onClick={() => handleResendInvite(userItem)}
                        disabled={resending}
                        className="text-emerald-600 dark:text-emerald-400 underline decoration-emerald-500/70 hover:text-emerald-700 disabled:opacity-50"
                      >
                        {resending ? 'Sending…' : 'Resend invite'}
                      </button>
                    ) : null}
                    {userItem.id !== user?.id ? (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        {userItem.active ? (
                          <button
                            type="button"
                            onClick={() => handleDeactivateUser(userItem.id)}
                            disabled={processing[userItem.id]}
                            className="text-orange-600 dark:text-orange-400 underline hover:text-orange-700 disabled:opacity-50"
                          >
                            {processing[userItem.id] === 'deactivating' ? '…' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleActivateUser(userItem.id)}
                            disabled={processing[userItem.id]}
                            className="text-green-600 dark:text-green-400 underline hover:text-green-700 disabled:opacity-50"
                          >
                            {processing[userItem.id] === 'activating' ? '…' : 'Activate'}
                          </button>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Supervisor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {allUsers.map((userItem) => (
                  <tr key={userItem.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {userItem.firstName} {userItem.lastName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">@{userItem.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {organizationLabel(userItem)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {userItem.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatPhone(userItem)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        userItem.role === 'ADMIN'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          : userItem.role === 'ORG_ADMIN'
                            ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {membershipLabel(userItem)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {userItem.supervisorName || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {userItem.verified ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Verified</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full">Pending</span>
                        )}
                        {userItem.active ? (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">Active</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Inactive</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {formatCreatedAt(userItem.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {canManageMembership && userItem.role !== 'ADMIN' && (isOrgAdmin || isPlatformAdmin || userItem.role !== 'ORG_ADMIN') && (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/users/${userItem.id}`)}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Edit profile
                          </button>
                        )}
                        {isPlatformAdmin && (
                          <button
                            type="button"
                            onClick={() => openEditUser(userItem)}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Edit
                          </button>
                        )}
                        {userItem.active ? (
                          <button
                            type="button"
                            onClick={() => handleDeactivateUser(userItem.id)}
                            disabled={processing[userItem.id] || userItem.id === user?.id}
                            className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                          >
                            {processing[userItem.id] === 'deactivating' ? '…' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleActivateUser(userItem.id)}
                            disabled={processing[userItem.id]}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {processing[userItem.id] === 'activating' ? '…' : 'Activate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {userTotal > 0 && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              disabled={userPage <= 0 || loading}
              onClick={() => setUserPage((page) => Math.max(0, page - 1))}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Page {userPage + 1} of {Math.max(1, Math.ceil(userTotal / USER_PAGE_SIZE))}
            </span>
            <button
              type="button"
              disabled={(userPage + 1) * USER_PAGE_SIZE >= userTotal || loading}
              onClick={() => setUserPage((page) => page + 1)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeEditUser} />
          <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit user
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  First name
                  <input
                    value={editDraft.firstName}
                    onChange={(e) => setEditDraft((prev) => ({ ...prev, firstName: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                  />
                </label>
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  Last name
                  <input
                    value={editDraft.lastName}
                    onChange={(e) => setEditDraft((prev) => ({ ...prev, lastName: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                  />
                </label>
              </div>
              <label className="block text-sm text-gray-600 dark:text-gray-300">
                Username
                <input
                  value={editDraft.username}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, username: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </label>
              <label className="block text-sm text-gray-600 dark:text-gray-300">
                Email
                <input
                  type="email"
                  value={editDraft.email}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, email: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </label>
              <label className="block text-sm text-gray-600 dark:text-gray-300">
                Phone
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={editDraft.phone}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="10-digit number"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </label>
              <label className="block text-sm text-gray-600 dark:text-gray-300">
                Date of birth
                <input
                  type="date"
                  value={editDraft.dateOfBirth}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEditUser}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveUserInfo}
                disabled={processing[editingUser.id]}
                className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {processing[editingUser.id] === 'updating-user' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'org-roles' && isOrgAdmin && (
        <OrganizationRolesPanel token={token} />
      )}

      {activeTab === 'org-workflows' && (isOrgAdmin || isPlatformAdmin) && (
        <OrgWorkflowReactionsPanel token={token} />
      )}

      {activeTab === 'activity' && (
        <ActivityTracker />
      )}

      {activeTab === 'broadcasts' && user?.role === 'ADMIN' && (
        <WorkloopBroadcastPanel />
      )}

      {activeTab === 'app-updates' && user?.role === 'ADMIN' && (
        <AppUpdateConfigPanel />
      )}

      {activeTab === 'news' && (user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
        <AdminNewsPanel />
      )}

      {activeTab === 'integrations' && (user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
        <AdminJiraSettingsPanel />
      )}

      {activeTab === 'scripts' && (user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
        <AdminRunScriptPanel />
      )}
    </div>
  );
};

export default AdminPanel;

